/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import React from 'react';
import { Typography, Box, Button, Divider, Link } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import { useTheme } from '@material-ui/core/styles';
import CardContent from '@material-ui/core/CardContent';
import colors from '../../themes/main-colors';
import { PageTextContentType } from '../../hooks/useContentMgmt';
import { Job } from '../../__generated__/pgeplus-types';

interface StepIndicatorProps {
  step: number;
}
export function StepIndicator({ step }: StepIndicatorProps) {
  return (
    <Box
      css={{
        borderRadius: '100%',
        backgroundColor: colors.noirBlurLight,
        minWidth: '28px',
        height: '28px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        align="center"
        css={{
          color: colors.white,
          fontSize: '20px',
          fontWeight: 'bold',
          fontFamily: 'Forma-DJR-Display',
        }}
      >
        {step}
      </Typography>
    </Box>
  );
}

interface StepProps {
  step: number;
  content: PageTextContentType;
  jobData?: Job | null;
  handleOnClickRestartApplication?: () => void;
  handleClickContinueWhereLeftOff?: () => void;
  handleClickGoToQuote?: () => void;
}

export function Step1({
  step,
  content,
  handleOnClickRestartApplication,
  handleClickContinueWhereLeftOff,
}: StepProps) {
  const theme = useTheme();
  return (
    <Card
      raised={false}
      component="div"
      css={{
        padding: '20px',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <CardContent
        css={{
          display: 'flex',
          padding: 0,
          paddingBottom: '0 !important',
        }}
      >
        <StepIndicator step={step} />
        <Box css={{ marginLeft: '20px' }}>
          <Typography
            component="h3"
            variant="h3"
            css={{
              fontFamily: 'Forma-DJR-Display',
              color: colors.noirBlur,
              letterSpacing: '0.4px',
              fontWeight: 'bold',
              fontSize: '1.25rem',
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                fontSize: '1rem',
              },
            }}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_1_HEADER')}
          </Typography>
          <Typography
            component="h4"
            variant="h4"
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              letterSpacing: 0,
              fontWeight: 'normal',
              fontSize: '1rem',
              marginTop: '5px',
            }}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_1_TEXT')}
          </Typography>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            css={css`
              padding: 0 28px;
              border-radius: 30px;
              font-size: 1rem;
              height: 3rem;
              background-color: ${colors.sparkBlue};
              color: ${colors.white};
              margin: 20px 0;
              text-align: center;
              &:hover {
                background-color: ${colors.buttonHoverPrimary};
              }
            `}
            onClick={handleClickContinueWhereLeftOff}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_CONTINUE_BUTTON')}
          </Button>
          <Divider css={{ background: colors.lightGray1 }} />
          <Typography
            component="h4"
            variant="h4"
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              letterSpacing: 0,
              fontWeight: 'normal',
              fontSize: '1rem',
              marginTop: '20px',
              marginBottom: '5px',
              lineHeight: '1.25rem',
            }}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_START_OVER_TEXT')}
          </Typography>
          <Link
            component="h2"
            variant="h2"
            onClick={handleOnClickRestartApplication}
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.sparkBlue,
              letterSpacing: 0,
              fontWeight: 'bold',
              fontSize: '1.25rem',
              lineHeight: '25px',
              cursor: 'pointer',
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                fontSize: '1rem',
              },
            }}
          >
            {content.get('DISCARD_MY_APPLICATION')}
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
}

export function Step2({ step, content, jobData }: StepProps) {
  const theme = useTheme();
  return (
    <Card
      raised={false}
      component="div"
      css={{
        padding: '20px',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <CardContent
        css={{
          display: 'flex',
          padding: 0,
          paddingBottom: '0 !important',
        }}
      >
        <StepIndicator step={step} />
        <Box css={{ marginLeft: '20px' }}>
          <Typography
            component="h3"
            variant="h3"
            css={{
              fontFamily: 'Forma-DJR-Display',
              color: colors.noirBlur,
              letterSpacing: '0.4px',
              fontWeight: 'bold',
              fontSize: '1.25rem',
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                fontSize: '1rem',
              },
            }}
          >
            {content.get(
              'INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_2_HEADER_INPROGRESS',
            )}
          </Typography>
          <Typography
            component="h4"
            variant="h4"
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              letterSpacing: 0,
              fontWeight: 'normal',
              fontSize: '1rem',
              marginTop: '15px',
            }}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_2_TEXT')}
          </Typography>
          <Typography
            component="h4"
            variant="h4"
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              letterSpacing: 0,
              fontWeight: 'bold',
              fontSize: '1rem',
              marginTop: '15px',
              marginBottom: '5px',
              lineHeight: '1.25rem',
            }}
          >
            {content.get(
              'INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_2_INSTALLER_CONTACT',
            )}
          </Typography>
          <Typography
            component="span"
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              letterSpacing: 0,
              fontSize: '1rem',
              lineHeight: '20px',
              cursor: 'pointer',
              margin: '10px 0',
              display: 'block',
            }}
          >
            {(jobData?.serviceProviderInfo?.firstName || '') +
              ' ' +
              (jobData?.serviceProviderInfo?.lastName || '')}
          </Typography>
          <Typography
            component="span"
            css={{
              fontFamily: 'Untitled-Sans',
              color: colors.noirBlur,
              letterSpacing: 0,
              fontSize: '1rem',
              lineHeight: '20px',
              cursor: 'pointer',
            }}
          >
            {jobData?.serviceProviderInfo?.phone}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export function Step3({ step, content, handleClickGoToQuote }: StepProps) {
  const theme = useTheme();
  return (
    <Card
      raised={false}
      component="div"
      css={{
        padding: '20px',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <CardContent
        css={{
          display: 'flex',
          padding: 0,
          paddingBottom: '0 !important',
        }}
      >
        <StepIndicator step={step} />
        <Box css={{ marginLeft: '20px', flexGrow: 1, marginTop: '4px' }}>
          <Typography
            component="h3"
            variant="h3"
            css={{
              fontFamily: 'Forma-DJR-Display',
              color: colors.noirBlur,
              letterSpacing: '0.4px',
              fontWeight: 'bold',
              fontSize: '1.25rem',
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                fontSize: '1rem',
              },
            }}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_3_HEADER')}
          </Typography>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            css={css`
              padding: 0 28px;
              border-radius: 30px;
              font-size: 1.125rem;
              height: 3rem;
              background-color: ${colors.sparkBlue};
              color: ${colors.white};
              margin-top: 12px;
              text-align: center;
              &:hover {
                background-color: ${colors.buttonHoverPrimary};
              }
              @media (max-width: ${theme.breakpoints.values.sm}px): {
                width: 100%;
                margin-top: 21px;
              },
            `}
            onClick={handleClickGoToQuote}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_STEP_3_BUTTON')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
